import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import { ReelAudioElement, formatMs } from '../reelCanvas.types';
import { LabeledSlider } from '../controls/EffectFields';
import { ReelMediaPicker } from './ReelMediaPicker';

export interface AudioElementDialogProps {
    open: boolean;
    element: ReelAudioElement | null;
    /** Open straight into the asset picker (used for a fresh audio component). */
    startWithPicker?: boolean;
    onClose: () => void;
    onChange: (patch: Partial<ReelAudioElement>) => void;
}

/** Audio SideKick settings: pick / replace the published audio asset, volume, loop, fades. */
export const AudioElementDialog: React.FC<AudioElementDialogProps> = ({ open, element, startWithPicker = false, onClose, onChange }) => {
    const theme = useTheme();
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        if (open) setPickerOpen(startWithPicker);
    }, [open, startWithPicker]);

    if (!element) return null;
    const hasAsset = Boolean(element.assetUrl);
    const pickerOnly = startWithPicker && !hasAsset;
    const clipLen = element.endMs - element.startMs;

    return (
        <>
            <Dialog open={open && !pickerOnly} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                    <AudiotrackIcon color="secondary" />
                    <Typography variant="h6" component="span" sx={{ fontWeight: 700, flex: 1 }} noWrap>
                        {element.name}
                    </Typography>
                    <IconButton size="small" aria-label="Close audio settings" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 1.5,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: alpha(theme.palette.secondary.main, 0.06),
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Typography variant="subtitle2" noWrap title={element.assetName ?? ''}>
                            {element.assetName ?? 'No audio selected'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {hasAsset
                                ? `${element.source === 'ae' ? 'Asset Management' : 'Beam on Screen'} library · plays ${formatMs(element.startMs)} → ${formatMs(element.endMs)} on the timeline`
                                : 'Pick a published audio file from your asset libraries.'}
                        </Typography>
                        {hasAsset && <audio key={element.assetUrl ?? ''} src={element.assetUrl ?? undefined} controls preload="metadata" style={{ width: '100%' }} />}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button variant="contained" color="secondary" startIcon={<AudiotrackIcon />} onClick={() => setPickerOpen(true)}>
                                {hasAsset ? 'Change audio' : 'Choose audio'}
                            </Button>
                            {hasAsset && (
                                <Button
                                    variant="text"
                                    color="error"
                                    size="small"
                                    onClick={() => onChange({ assetId: null, assetUrl: null, publishedRelativeUrl: null, assetName: null, mimeType: null })}
                                >
                                    Remove audio
                                </Button>
                            )}
                        </Box>
                    </Box>

                    <Divider />
                    <LabeledSlider label="Volume" value={element.volume} min={0} max={100} unit="%" onChange={(v) => onChange({ volume: v })} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2.5 }}>
                        <LabeledSlider
                            label="Fade in"
                            value={element.fadeInMs}
                            min={0}
                            max={Math.max(0, Math.min(10000, clipLen))}
                            step={100}
                            unit="ms"
                            onChange={(v) => onChange({ fadeInMs: v })}
                        />
                        <LabeledSlider
                            label="Fade out"
                            value={element.fadeOutMs}
                            min={0}
                            max={Math.max(0, Math.min(10000, clipLen))}
                            step={100}
                            unit="ms"
                            onChange={(v) => onChange({ fadeOutMs: v })}
                        />
                    </Box>
                    <FormControlLabel
                        control={<Switch size="small" color="secondary" checked={element.loop} onChange={(e) => onChange({ loop: e.target.checked })} />}
                        label="Loop inside the clip when the audio is shorter"
                    />
                    <Typography variant="caption" color="text.secondary">
                        Audio is not drawn on the canvas. Drag its clip on the timeline to decide when it plays; it follows Play / Pause and scrubbing.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="secondary" onClick={onClose}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <ReelMediaPicker
                open={open && pickerOpen}
                assetTypes={['AUDIO']}
                title="Select published audio"
                initialSource={element.source ?? undefined}
                onCancel={() => {
                    setPickerOpen(false);
                    if (pickerOnly) onClose();
                }}
                onSelect={(sel) => {
                    onChange({
                        assetId: sel.assetId,
                        assetUrl: sel.assetUrl,
                        publishedRelativeUrl: sel.publishedRelativeUrl,
                        assetName: sel.assetName,
                        mimeType: sel.mimeType,
                        source: sel.source,
                    });
                    setPickerOpen(false);
                    if (pickerOnly) onClose();
                }}
            />
        </>
    );
};

export default AudioElementDialog;
